import { reducer } from '@ardoq/rxbeach';
import { AppModelStateEditStreamShape } from 'appModelStateEdit/types';
import {
  BeginCreateWorkflowPayload,
  BeginEditWorkflowPayload,
  SetEnhancedScopeDataPayload,
  beginCreateWorkflow,
  beginEditWorkflow,
  finishWorkflowReducer,
  setEnhancedScopeData,
} from 'appModelStateEdit/actions';
import { reducers as propertiesEditorReducers } from 'appModelStateEdit/propertiesEditor/reducers';
import { APIEntityType, ArdoqId } from '@ardoq/api-types';
import {
  notifyComponentLocked,
  notifyComponentUnlocked,
} from 'streams/components/ComponentActions';
import {
  notifyReferenceLocked,
  notifyReferenceUnlocked,
} from 'streams/references/ReferenceActions';
import { updateScopeDataProperties } from 'appModelStateEdit/reducerUtils';
import { GetContentOptions } from 'appModelStateEdit/legacyTypes';

import type { PropertyValue } from 'aqTypes';
import {
  setRefTypesSortedByMostUsed,
  SetRefTypesSortedByMostUsedPayload,
} from './propertiesEditor/actions';

export const defaultState: AppModelStateEditStreamShape = {
  newEntityAttributes: null,
  entityType: null,
  entityIDs: [],
  enhancedScopeData: null,
  originalEnhancedScopeData: null,
  addedTags: new Set(),
  dirtyAttributes: new Set(),
  options: {} as GetContentOptions,
  stashedStates: [],
  errorMessage: null,
};

const beginEditWorkflowReducer = (
  { stashedStates }: AppModelStateEditStreamShape,
  payload: BeginEditWorkflowPayload
): AppModelStateEditStreamShape => {
  return {
    ...defaultState,
    ...payload,
    stashedStates,
    originalEnhancedScopeData: payload.enhancedScopeData,
  };
};

const beginCreateWorkflowReducer = (
  { stashedStates }: AppModelStateEditStreamShape,
  payload: BeginCreateWorkflowPayload
): AppModelStateEditStreamShape => {
  return {
    ...defaultState,
    ...payload,
    stashedStates,
    originalEnhancedScopeData: payload.enhancedScopeData,
  };
};

const resetWorkflowStateExceptStashedStates = ({
  stashedStates,
}: AppModelStateEditStreamShape) => {
  return { ...defaultState, stashedStates };
};

const setEnhancedScopeDataReducer = (
  state: AppModelStateEditStreamShape,
  { enhancedScopeData }: SetEnhancedScopeDataPayload
): AppModelStateEditStreamShape => {
  return {
    ...state,
    enhancedScopeData,
    originalEnhancedScopeData: enhancedScopeData,
  };
};
const updateScopeDataReducer =
  (
    entityType: APIEntityType,
    propertiesToUpdate: Record<string, PropertyValue>
  ) =>
  (state: AppModelStateEditStreamShape, id: ArdoqId) => {
    if (!state.entityType || !state.enhancedScopeData) {
      return { ...state };
    }
    if (state.entityType !== entityType) {
      return { ...state };
    }
    if (!state.entityIDs.includes(id)) {
      return { ...state };
    }
    return {
      ...state,
      enhancedScopeData: updateScopeDataProperties(
        state.entityType,
        [id],
        state.enhancedScopeData,
        propertiesToUpdate
      ),
    };
  };

const notifyComponentLockedReducer = updateScopeDataReducer(
  APIEntityType.COMPONENT,
  { lock: true }
);
const notifyComponentUnlockedReducer = updateScopeDataReducer(
  APIEntityType.COMPONENT,
  { lock: null }
);

const notifyReferenceLockedReducer = updateScopeDataReducer(
  APIEntityType.REFERENCE,
  { lock: true }
);

const notifyReferenceUnlockedReducer = updateScopeDataReducer(
  APIEntityType.REFERENCE,
  { lock: null }
);

const setRefTypesSortedByMostUsedReducer = (
  currentState: AppModelStateEditStreamShape,
  { newValue }: SetRefTypesSortedByMostUsedPayload
): AppModelStateEditStreamShape => {
  return {
    ...currentState,
    refTypesSortedByMostUsed: newValue,
  };
};

export const reducers = [
  reducer(beginEditWorkflow, beginEditWorkflowReducer),
  reducer(beginCreateWorkflow, beginCreateWorkflowReducer),
  reducer(finishWorkflowReducer, resetWorkflowStateExceptStashedStates),
  reducer(setEnhancedScopeData, setEnhancedScopeDataReducer),
  reducer(notifyComponentLocked, notifyComponentLockedReducer),
  reducer(notifyComponentUnlocked, notifyComponentUnlockedReducer),
  reducer(notifyReferenceLocked, notifyReferenceLockedReducer),
  reducer(notifyReferenceUnlocked, notifyReferenceUnlockedReducer),
  reducer(setRefTypesSortedByMostUsed, setRefTypesSortedByMostUsedReducer),
  ...propertiesEditorReducers,
];
